"use client";

import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const consultationPerks = ["Bezpłatna wizja lokalna i ocena stanu dachu", "Indywidualnie dobrana technologia hydroizolacji", "Gwarancja szczelności i trwałości powłoki", "Wsparcie techniczne na każdym etapie realizacji"];
const consultationTypes = ["Hydroizolacja dachu płaskiego", "Renowacja dachu skośnego", "Uszczelnienie tarasu/balkonu", "Inne"];
const michelinOptions = ["Dom jednorodzinny", "Obiekt przemysłowy/hala", "Wspólnota mieszkaniowa", "Deweloper", "Inne"];
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border border-border p-8 rounded-lg">
        <p className="font-mono text-xs uppercase tracking-widest text-blue-600 mb-4">
          [ ZAPYTANIE WYSŁANE ]
        </p>
        <p className="font-default text-2xl text-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg">
          Wyślij kolejne zapytanie
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="name" rules={{
        required: "Imię i nazwisko jest wymagane",
        minLength: {
          value: 2,
          message: "Min 2 znaki"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Imię i nazwisko
              </FormLabel>
              <FormControl>
                <Input placeholder="Jan Kowalski" className="border-0 border-b border-slate-300 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-blue-600 h-10 font-default text-base text-foreground placeholder:text-muted-foreground/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="restaurant_name" rules={{
        required: "Nazwa firmy lub adres inwestycji jest wymagany"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Firma / Adres inwestycji
              </FormLabel>
              <FormControl>
                <Input placeholder="HydroRoof" className="border-0 border-b border-slate-300 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-blue-600 h-10 font-default text-base text-foreground placeholder:text-muted-foreground/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email jest wymagany",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Nieprawidłowy adres email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Email
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="biuro@budsys.pl" className="border-0 border-b border-slate-300 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-blue-600 h-10 font-default text-base text-foreground placeholder:text-muted-foreground/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Numer telefonu jest wymagany"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Telefon
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+48 796 280 315" className="border-0 border-b border-slate-300 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-blue-600 h-10 font-default text-base text-foreground placeholder:text-muted-foreground/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="michelin_status" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Typ obiektu
              </FormLabel>
              <FormControl>
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="border-0 border-b border-slate-300 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-blue-600 h-10 font-default text-base text-foreground w-full">
                    <SelectValue placeholder="Wybierz typ" />
                  </SelectTrigger>
                  <SelectContent>
                    {michelinOptions.map((opt, i) => <SelectItem data-index={i} key={opt} value={opt}>
                        {opt}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferred_date" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Preferowana data kontaktu
              </FormLabel>
              <FormControl>
                <Input type="date" className="border-0 border-b border-slate-300 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-blue-600 h-10 font-default text-base text-foreground placeholder:text-muted-foreground/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="consultation_type" rules={{
      required: "Wybierz rodzaj usługi"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Rodzaj usługi
            </FormLabel>
            <FormControl>
              <RadioGroup value={field.value} onValueChange={field.onChange} className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                {consultationTypes.map((type, i) => <label data-index={i} key={type} className="flex items-center gap-3 cursor-pointer font-default text-sm text-foreground hover:text-blue-600 transition-colors">
                    <RadioGroupItem value={type} />
                    {type}
                  </label>)}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Wiadomość
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Opisz swój problem z dachem lub zakres prac..." className="border-0 border-b border-slate-300 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-blue-600 min-h-24 font-default text-base text-foreground placeholder:text-muted-foreground/50 resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="acknowledgement" rules={{
      required: "Prosimy o akceptację zgody"
    }} render={({
      field
    }) => <FormItem>
            <div className="flex items-start gap-3">
              <FormControl>
                <Checkbox checked={!!field.value} onCheckedChange={field.onChange} className="mt-0.5" />
              </FormControl>
              <FormLabel className="font-default text-[11px] text-muted-foreground leading-relaxed cursor-pointer">
                Wyrażam zgodę na przetwarzanie moich danych osobowych w celu przygotowania wyceny.
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="border border-red-600/40 p-4 rounded-lg">
          <p className="font-mono text-xs uppercase tracking-wider text-red-600">{submitError}</p>
        </div>}

      <div className="pt-4 space-y-4">
        <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto font-default text-sm uppercase tracking-[0.1em] h-12 px-8 bg-blue-600 text-white hover:bg-blue-700 rounded-lg">
          {isSubmitting ? "Wysyłanie..." : "Zamów bezpłatną wycenę →"}
        </Button>

        <div className="flex items-center gap-2">
          <span className="relative flex size-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-lg bg-blue-600 opacity-75"></span>
            <span className="relative inline-flex size-2 rounded-lg bg-blue-600"></span>
          </span>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Czas odpowiedzi: do 24 godzin
          </p>
        </div>
      </div>
    </Form>;
}
export default function Consultation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="5278" id="formularz-bezpłatnej-wyceny" className="bg-slate-50 text-foreground pt-40 pb-24 px-6 md:px-12 lg:px-20 relative overflow-hidden">
      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20">
          <div className="flex flex-col justify-between">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-blue-600 mb-8">
                [ WYCENA ]
              </p>
              <h2 className="font-default font-bold text-5xl md:text-6xl lg:text-[64px] leading-[1.05] text-foreground mb-8">
                Zamów <em className="italic font-light">bezpłatną</em> wycenę
              </h2>
              <p className="font-default text-base md:text-lg text-muted-foreground leading-relaxed max-w-md mb-12">
                Zadbaj o trwałość swojego dachu dzięki profesjonalnej hydroizolacji. 
                Prześlij nam szczegóły swojego projektu, a my przygotujemy indywidualną ofertę 
                dostosowaną do Twoich potrzeb.
              </p>

              <ul className="space-y-4 mb-12">
                {consultationPerks.map((perk, i) => <li data-index={i} key={perk} className="flex items-start gap-4">
                    <Check className="size-4 text-blue-600 mt-0.5 shrink-0" strokeWidth={2.5} />
                    <span className="font-default text-sm text-foreground/90">
                      {perk}
                    </span>
                  </li>)}
              </ul>
            </div>

            <div className="pt-8 border-t border-slate-200">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                Kontakt bezpośredni:{" "}
                <Link to="mailto:biuro@budsys.pl" className="text-blue-600 hover:text-blue-800 transition-colors">biuro@budsys.pl</Link>
              </p>
            </div>

            <div className="cursor-pointer mt-6" role="link" tabIndex={0} onClick={() => window.open("https://wizytowka.rzetelnafirma.pl/6ACNYO29S9HRCY1S", "_blank", "noopener")}>
              <img src="https://aktywnybaner.rzetelnafirma.pl/ActiveBanner/GetActiveBannerImage/44/6ACNYO29S9HRCY1S" style={{
              border: "none"
            }} alt="Rzetelna Firma - baner aktywny" />
            </div>
          </div>

          <div className="relative">
            <div className="absolute -top-20 -right-20 size-72 rounded-lg opacity-20 blur-3xl pointer-events-none" style={{
            backgroundColor: "hsl(200 95% 45%)"
          }} aria-hidden="true" />

            <div className="relative border border-slate-200 p-8 md:p-10 bg-white rounded-lg shadow-lg">
              <span className="absolute -top-px -left-px size-4 border-t-2 border-l-2 border-blue-600" aria-hidden="true" />
              <span className="absolute -top-px -right-px size-4 border-t-2 border-r-2 border-blue-600" aria-hidden="true" />
              <span className="absolute -bottom-px -left-px size-4 border-b-2 border-l-2 border-blue-600" aria-hidden="true" />
              <span className="absolute -bottom-px -right-px size-4 border-b-2 border-r-2 border-blue-600" aria-hidden="true" />

              <div className="flex items-center justify-between mb-8 pb-4 border-b border-slate-200"><p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground" data-ast-id="f:/src/pages/Contact/sections/Consultation.tsx|sig:27bfew" data-ast-start="13251" data-ast-end="13393" zn_id="525">Formularz</p><p className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-600" data-ast-id="f:/src/pages/Contact/sections/Consultation.tsx|sig:mngh4b" data-ast-start="13410" data-ast-end="13549" zn_id="526">Bezpieczne połączenie</p></div>

              <FormProvider formKey="estimate_form_contact" formId="estimate_form_contact" sectionName="consultation-inquiry" defaultValues={{
              name: "",
              email: "",
              phone: "",
              restaurant_name: "",
              consultation_type: "",
              preferred_date: "",
              message: "",
              michelin_status: "",
              acknowledgement: false
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true
              },
              restaurant_name: {
                required: true
              },
              consultation_type: {
                required: true
              }
            }} submitText="Zamów wycenę" successMessage="Dziękujemy. Nasz zespół skontaktuje się z Tobą w ciągu 24 godzin." errorMessage="Wystąpił błąd podczas wysyłania. Spróbuj ponownie lub napisz do nas bezpośrednio.">
                <ConsultationForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}